import React, { useState, useEffect } from 'react';
import Pagination from 'react-js-pagination';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Form, Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { TIMELINE_API } from '@app/utils/apiUrl.js';
import { Get } from '@app/utils/httpProvider';
import { createUrlWithParam, formatDateTimeMoment } from '@app/utils/helper';
import { URI_INTERNAL } from '@app/utils/pathLocations';
import { objectShowNumber } from '@app/utils/define';

const dateColors = [
    { bg: '#dd4b39', text: '#ffffff' }, // red (danger)
    { bg: '#00a65a', text: '#ffffff' }, // green (success)
    { bg: '#00c0ef', text: '#ffffff' }, // aqua/info (cyan)
    { bg: '#f39c12', text: '#ffffff' }, // yellow/warning (orange)
    { bg: '#3c8dbc', text: '#ffffff' }, // blue (primary)
    { bg: '#605ca8', text: '#ffffff' }, // purple
    { bg: '#f012be', text: '#ffffff' }, // fuchsia
    { bg: '#39cccc', text: '#ffffff' }  // teal
];


const formatTimeLabel = (dateStr) => {
    if (!dateStr) return '';
    const m = moment(dateStr);
    return m.isValid() ? m.format('HH:mm') : '';
};

const Timeline = () => {
    const history = useHistory();
    const valueSearch = useSelector((state) => state.search.valueSearch);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [actionType, setActionType] = useState('');
    const [expandedLogs, setExpandedLogs] = useState({});

    const { isLoading, isError, error, data, refetch } = useQuery(
        ['assetTimelineData', valueSearch, page, pageSize, actionType],
        () => {
            const params = {
                Keyword: valueSearch ? valueSearch : '',
                PageIndex: page,
                PageSize: pageSize
            };
            if (actionType) {
                params.ActionType = actionType;
            }
            return Get(TIMELINE_API.GET_PAGING, null, params);
        }
    );

    useEffect(() => {
        refetch();
    }, [pageSize, page, actionType, valueSearch]);

    const changePage = (number) => {
        setPage(number);
    };

    const onChangeShowNumber = (e) => {
        setPageSize(Number(e.target.value));
        setPage(1);
    };

    const toggleExpand = (id) => {
        setExpandedLogs((prev) => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const getNormalizedType = (type) => {
        const t = type?.toUpperCase() || '';
        if (t.includes('QR')) return 'QR';
        if (t.includes('CREATE') || t.includes('ADD')) return 'CREATE';
        if (t.includes('DELETE') || t.includes('REMOVE') || t.includes('DESTROY')) return 'DELETE';
        if (t.includes('TRANSFER') || t.includes('MOVE')) return 'TRANSFER';
        if (t.includes('MAINTENANCE') || t.includes('REPAIR') || t.includes('FIX')) return 'MAINTENANCE';
        if (t.includes('UPDATE') || t.includes('EDIT') || t.includes('MODIFY')) return 'UPDATE';
        return t;
    };

    const getBadgeVariant = (type) => {
        const norm = getNormalizedType(type);
        switch (norm) {
            case 'QR':
                return 'info';
            case 'CREATE':
                return 'success';
            case 'UPDATE':
                return 'primary';
            case 'DELETE':
                return 'danger';
            case 'TRANSFER':
                return 'warning';
            case 'MAINTENANCE':
                return 'info';
            default:
                return 'secondary';
        }
    };

    const getBadgeText = (type) => {
        const norm = getNormalizedType(type);
        switch (norm) {
            case 'QR':
                return 'Gán mã QR';
            case 'CREATE':
                return 'Thêm mới';
            case 'UPDATE':
                return 'Cập nhật';
            case 'DELETE':
                return 'Xóa';
            case 'TRANSFER':
                return 'Điều chuyển';
            case 'MAINTENANCE':
                return 'Bảo trì';
            default:
                return type || 'Khác';
        }
    };

    const getMarkerStyle = (type) => {
        const norm = getNormalizedType(type);
        switch (norm) {
            case 'QR':
                return { bg: '#605ca8', icon: 'fas fa-qrcode' }; // purple
            case 'CREATE':
                return { bg: '#00a65a', icon: 'fas fa-plus' }; // green
            case 'UPDATE':
                return { bg: '#3c8dbc', icon: 'fas fa-pencil-alt' }; // blue
            case 'DELETE':
                return { bg: '#dd4b39', icon: 'fas fa-trash-alt' }; // red
            case 'TRANSFER':
                return { bg: '#f39c12', icon: 'fas fa-exchange-alt' }; // orange
            case 'MAINTENANCE':
                return { bg: '#00c0ef', icon: 'fas fa-tools' }; // cyan
            default:
                return { bg: '#605ca8', icon: 'fas fa-history' }; // purple
        }
    };

    const parseJson = (str) => {
        try {
            return JSON.parse(str);
        } catch (e) {
            return null;
        }
    };

    const translateField = (field) => {
        const dict = {
            assetCode: 'Mã tài sản',
            assetName: 'Tên tài sản',
            serialNumber: 'Số serial',
            model: 'Model',
            manufacturer: 'Nhà sản xuất',
            purchaseDate: 'Ngày mua',
            purchaseValue: 'Giá trị mua',
            notes: 'Ghi chú',
            manager: 'Người quản lý',
            currentDepartmentId: 'Mã phòng ban hiện tại',
            assetGroupId: 'Mã nhóm tài sản',
            assetStatusId: 'Mã trạng thái tài sản',
            statusName: 'Tên trạng thái',
            departmentName: 'Tên phòng ban',
            isDisposed: 'Trạng thái thanh lý',
            performedBy: 'Người thực hiện',
            performedAt: 'Thời gian thực hiện'
        };
        return dict[field] || field;
    };

    const formatValue = (val) => {
        if (val === undefined || val === null) return '—';
        if (typeof val === 'boolean') return val ? 'Có' : 'Không';
        if (typeof val === 'object') return JSON.stringify(val);
        return String(val);
    };

    const renderValueDiff = (oldValStr, newValStr) => {
        const oldObj = parseJson(oldValStr);
        const newObj = parseJson(newValStr);
        if (!oldObj && !newObj) return null;

        const allKeys = Array.from(
            new Set([
                ...Object.keys(oldObj || {}),
                ...Object.keys(newObj || {})
            ])
        ).filter(
            (k) =>
                k !== 'id' &&
                k !== 'createdDate' &&
                k !== 'modifiedDate' &&
                k !== 'modifiedBy' &&
                k !== 'createdBy'
        );

        const diffs = [];
        allKeys.forEach((k) => {
            const oVal = oldObj ? oldObj[k] : undefined;
            const nVal = newObj ? newObj[k] : undefined;
            if (JSON.stringify(oVal) !== JSON.stringify(nVal)) {
                diffs.push({
                    field: k,
                    old: oVal,
                    new: nVal
                });
            }
        });

        if (diffs.length === 0) return <div className="text-muted small mt-2">Không phát hiện thay đổi thuộc tính cụ thể.</div>;

        return (
            <div className="mt-3 p-3 bg-light rounded border shadow-sm animate__animated animate__fadeIn">
                <div className="fw-bold mb-2 text-secondary small text-uppercase" style={{ letterSpacing: '0.5px' }}>
                    Chi tiết thay đổi
                </div>
                <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0 bg-white shadow-xs">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '30%' }}>Thuộc tính</th>
                                <th style={{ width: '35%' }}>Giá trị cũ</th>
                                <th style={{ width: '35%' }}>Giá trị mới</th>
                            </tr>
                        </thead>
                        <tbody>
                            {diffs.map((d, idx) => (
                                <tr key={idx}>
                                    <td className="fw-bold text-dark small">{translateField(d.field)}</td>
                                    <td className="text-danger small" style={{ textDecoration: 'line-through' }}>
                                        {formatValue(d.old)}
                                    </td>
                                    <td className="text-success small fw-semibold">
                                        {formatValue(d.new)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    <Card className="shadow-sm">
                        <Card.Header className="bg-white py-3">
                            <h5 className="card-title fw-bold text-primary mb-0 d-flex align-items-center">
                                <i className="fas fa-history mr-2" />
                                Timeline Hoạt Động Tài Sản
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            {/* Filter Bar */}
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center mb-4 gap-3 bg-light p-3 rounded shadow-xs">
                                <Form className="form-inline d-flex align-items-center gap-2">
                                    <Form.Label className="mb-0 text-nowrap mr-2">Hiển thị:</Form.Label>
                                    <Form.Control
                                        as="select"
                                        size="sm"
                                        style={{ width: 'auto' }}
                                        value={pageSize}
                                        onChange={onChangeShowNumber}
                                    >
                                        {objectShowNumber.map((item, key) => (
                                            <option key={Number(key)} value={item.value}>
                                                {item.text}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form>

                                <Form className="form-inline d-flex align-items-center gap-2 ml-md-auto">
                                    <Form.Label className="mb-0 text-nowrap mr-2">Loại tác vụ:</Form.Label>
                                    <Form.Control
                                        as="select"
                                        size="sm"
                                        style={{ width: '180px' }}
                                        value={actionType}
                                        onChange={(e) => {
                                            setActionType(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <option value="">-- Tất cả --</option>
                                        <option value="CREATE">Thêm mới</option>
                                        <option value="UPDATE">Cập nhật</option>
                                        <option value="DELETE">Xóa</option>
                                        <option value="TRANSFER">Điều chuyển</option>
                                        <option value="MAINTENANCE">Bảo trì</option>
                                    </Form.Control>
                                </Form>
                            </div>

                            {/* Timeline Content */}
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" className="mb-2" />
                                    <div>Đang tải dữ liệu...</div>
                                </div>
                            ) : isError ? (
                                <div className="alert alert-danger shadow-xs">
                                    Có lỗi xảy ra: {error?.message || 'Không thể lấy dữ liệu timeline'}
                                </div>
                            ) : data?.data?.length > 0 ? (
                                <div className="timeline-container">
                                    <div className="custom-timeline">
                                        {(() => {
                                            let lastDate = null;
                                            let dateCounter = -1;
                                            return data.data.map((log) => {
                                                const isExpanded = !!expandedLogs[log.id];
                                                const hasDetails = log.oldValue || log.newValue;
                                                const logDate = moment(log.performedAt).format('DD/MM/YYYY');
                                                const showDateLabel = logDate !== lastDate;
                                                if (showDateLabel) {
                                                    lastDate = logDate;
                                                    dateCounter += 1;
                                                }
                                                const marker = getMarkerStyle(log.actionType);
                                                const dateStyle = dateColors[dateCounter % dateColors.length];

                                                return (
                                                    <React.Fragment key={log.id}>
                                                        {showDateLabel && (
                                                            <div className="timeline-date-label">
                                                                <span style={{ backgroundColor: dateStyle.bg, color: dateStyle.text }}>
                                                                    {logDate}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="timeline-item">
                                                            <div className="timeline-marker" style={{ backgroundColor: marker.bg }}>
                                                                <i className={marker.icon} style={{ color: '#fff' }} />
                                                            </div>
                                                            <div className="timeline-content card shadow-xs border-light mb-3">
                                                                <div className="card-body p-3">
                                                                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
                                                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                            <Badge bg={getBadgeVariant(log.actionType)} className="text-uppercase py-1.5 px-2">
                                                                                {getBadgeText(log.actionType)}
                                                                            </Badge>
                                                                            <span className="fw-semibold text-dark text-decoration-none">
                                                                                Thực hiện bởi:{' '}
                                                                                <span className="text-primary font-weight-bold">
                                                                                    {log.performedBy}
                                                                                </span>
                                                                            </span>
                                                                            {log.assetCode && (
                                                                                <button
                                                                                    onClick={() =>
                                                                                        history.push(
                                                                                            createUrlWithParam(
                                                                                                URI_INTERNAL.ASSET_EDIT_PATH,
                                                                                                { id: log.assetId }
                                                                                            )
                                                                                        )
                                                                                    }
                                                                                    className="btn btn-link btn-sm p-0 ml-2 text-decoration-none text-info fw-bold"
                                                                                >
                                                                                    <i className="fas fa-link mr-1" />
                                                                                    {log.assetCode} - {log.assetName}
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-muted small" title={formatDateTimeMoment(log.performedAt)}>
                                                                            <i className="far fa-clock mr-1" />
                                                                            {formatTimeLabel(log.performedAt)}
                                                                        </div>
                                                                    </div>
                                                                    <p className="mb-0 text-secondary fw-normal">{log.description}</p>

                                                                    {hasDetails && (
                                                                        <div className="mt-2">
                                                                            <button
                                                                                onClick={() => toggleExpand(log.id)}
                                                                                className="btn btn-outline-secondary btn-xs py-1 px-2.5 rounded-pill d-flex align-items-center gap-1.5 font-weight-medium"
                                                                                style={{ fontSize: '11px', letterSpacing: '0.2px' }}
                                                                            >
                                                                                <i className={isExpanded ? "fas fa-chevron-up" : "fas fa-chevron-down"} />
                                                                                <span>{isExpanded ? "Thu gọn chi tiết" : "Xem chi tiết thay đổi"}</span>
                                                                            </button>
                                                                            {isExpanded && renderValueDiff(log.oldValue, log.newValue)}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            });
                                        })()}
                                    </div>

                                    {/* Pagination */}
                                    <div className="d-flex justify-content-end mt-4">
                                        <Pagination
                                            itemClass="page-item"
                                            linkClass="page-link"
                                            innerClass="pagination pagination-sm no-margin"
                                            activePage={page}
                                            itemsCountPerPage={pageSize}
                                            totalItemsCount={data.totalRecords}
                                            pageRangeDisplayed={5}
                                            onChange={changePage}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-5 text-muted">
                                    <i className="fas fa-clipboard-list fa-3x mb-3 text-light" />
                                    <div>Không tìm thấy dữ liệu hoạt động nào phù hợp.</div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/* Custom Styles */}
            <style jsx>{`
                .custom-timeline {
                    position: relative;
                    padding-left: 30px;
                    margin-top: 15px;
                }
                .custom-timeline::before {
                    content: '';
                    position: absolute;
                    left: 9px;
                    top: 5px;
                    bottom: 5px;
                    width: 2px;
                    background: #e9ecef;
                }
                .timeline-date-label {
                    position: relative;
                    margin-left: -30px;
                    margin-bottom: 18px;
                    margin-top: 18px;
                    display: flex;
                    z-index: 2;
                }
                .timeline-date-label span {
                    border-radius: 4px;
                    padding: 5px 10px;
                    font-size: 12.5px;
                    font-weight: 600;
                    display: inline-block;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.15);
                    letter-spacing: 0.3px;
                }
                .timeline-item {
                    position: relative;
                    margin-bottom: 20px;
                    padding-left: 10px;
                }
                .timeline-marker {
                    position: absolute;
                    left: -36px;
                    top: 10px;
                    width: 30px;
                    height: 30px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.15);
                }
                .timeline-marker i {
                    font-size: 13px;
                    color: #fff;
                }
                .gap-2 {
                    gap: 0.5rem;
                }
                .gap-3 {
                    gap: 1rem;
                }
                .shadow-xs {
                    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.05);
                }
                .btn-xs {
                    font-size: 10.5px;
                    padding: 2px 6px;
                }
                .font-weight-medium {
                    font-weight: 500;
                }
            `}</style>
        </div>
    );
};

export default Timeline;
